import {
  AiPromptUseCase,
  buildExternalUnmutedUseCaseId,
  type AiPromptUseCaseId,
} from '../admin/types/AiPromptConfig';

export const SETUP_QUERY = 'setup';

export const SETUP_CHANNELS = [
  'whatsapp',
  'instagram',
  'messenger',
  'telegram',
  'tiktok',
  'webchat',
] as const;

export type SetupChannelId = (typeof SETUP_CHANNELS)[number];

export type SetupInstructionKey = 'all' | SetupChannelId | 'instagram-comments' | 'whatsapp-groups';

export type SetupStep =
  | { kind: 'welcome' }
  | { kind: 'plan' }
  | { kind: 'channels' }
  | { kind: 'connect'; channel: SetupChannelId }
  | { kind: 'business-hours' }
  | { kind: 'instruction'; key: SetupInstructionKey; useCaseId: AiPromptUseCaseId }
  | { kind: 'done' };

export type SetupPhase = 'active' | 'paused' | 'done';

export interface SetupRecord {
  phase: SetupPhase;
  stepIndex: number;
  channels: SetupChannelId[];
}

const STORAGE_PREFIX = 'shahagent.workspaceSetup.v1.';
const CHANGE_EVENT = 'shahagent-workspace-setup-changed';

const CHANNEL_USE_CASE: Record<SetupChannelId, AiPromptUseCaseId> = {
  whatsapp: buildExternalUnmutedUseCaseId('whatsapp'),
  instagram: buildExternalUnmutedUseCaseId('instagram'),
  messenger: buildExternalUnmutedUseCaseId('messenger'),
  telegram: buildExternalUnmutedUseCaseId('telegram'),
  tiktok: buildExternalUnmutedUseCaseId('tiktok'),
  webchat: buildExternalUnmutedUseCaseId('webchat'),
};

export const isSetupChannelId = (value: string): value is SetupChannelId =>
  (SETUP_CHANNELS as readonly string[]).includes(value);

export const connectorTabForChannel = (channel: SetupChannelId): string => {
  if (channel === 'whatsapp') {
    return 'whatsapp-app';
  }

  return channel;
};

export const buildSetupSteps = (channels: readonly SetupChannelId[]): SetupStep[] => {
  const selected = SETUP_CHANNELS.filter(channel => channels.includes(channel));
  const steps: SetupStep[] = [
    { kind: 'welcome' },
    { kind: 'plan' },
    { kind: 'channels' },
    ...selected.map(channel => ({ kind: 'connect' as const, channel })),
    { kind: 'business-hours' },
    { kind: 'instruction', key: 'all', useCaseId: AiPromptUseCase.ReplyExternalUnmuted },
    ...selected.map(channel => ({
      kind: 'instruction' as const,
      key: channel,
      useCaseId: CHANNEL_USE_CASE[channel],
    })),
  ];

  if (selected.includes('instagram')) {
    steps.push({
      kind: 'instruction',
      key: 'instagram-comments',
      useCaseId: AiPromptUseCase.ReplyInstagramComment,
    });
  }

  if (selected.includes('whatsapp')) {
    steps.push({
      kind: 'instruction',
      key: 'whatsapp-groups',
      useCaseId: AiPromptUseCase.ReplyWhatsAppGroup,
    });
  }

  steps.push({ kind: 'done' });
  return steps;
};

export const emptySetupRecord = (): SetupRecord => ({
  phase: 'active',
  stepIndex: 0,
  channels: [],
});

const storageKey = (tenantId: string): string => `${STORAGE_PREFIX}${tenantId}`;

const normalizeRecord = (value: unknown): SetupRecord | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Partial<SetupRecord>;
  const phase = record.phase === 'paused' || record.phase === 'done' ? record.phase : 'active';
  const channels = Array.isArray(record.channels)
    ? record.channels.filter((channel): channel is SetupChannelId => typeof channel === 'string' && isSetupChannelId(channel))
    : [];
  const stepIndex = Number.isFinite(record.stepIndex) ? Math.max(0, Math.floor(record.stepIndex as number)) : 0;

  return { phase, stepIndex, channels };
};

export const readSetupRecord = (tenantId: string): SetupRecord | null => {
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (!raw) {
      return null;
    }

    return normalizeRecord(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const writeSetupRecord = (tenantId: string, record: SetupRecord): void => {
  try {
    localStorage.setItem(storageKey(tenantId), JSON.stringify(record));
  } catch {
    // Progress is a convenience. The guide still works for this visit if storage is blocked.
  }

  window.dispatchEvent(new Event(CHANGE_EVENT));
};

export const isSetupPending = (tenantId: string): boolean => {
  const record = readSetupRecord(tenantId);
  return record !== null && record.phase !== 'done';
};

export const clampStepIndex = (record: SetupRecord): number => {
  const steps = buildSetupSteps(record.channels);
  return Math.min(record.stepIndex, Math.max(0, steps.length - 1));
};

export const subscribeSetupChanges = (listener: () => void): (() => void) => {
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener('storage', listener);
  };
};
